import React from "react";
import { useApolloClient } from "@apollo/client";
import _get from "lodash/get";
import * as Loading from "@repoflow/components/loading";
import * as SectionQL from "queries/section";
import * as SiteQL from "queries/site";
import * as PageRender from "@common/render/page";
import { fetchBlogData } from "render/blog-data";
import { siteFileBaseOf, FILE_PREFIX } from "@common/schema/site-file/url";

// The composed document, framed.
//
// This was a screen of its own — `/site/:siteId/page/:pageId/preview`, reached
// by a Preview button on the page's screen — and it is a component because two
// screens show the same thing now: a page's, over the page, and a section's,
// over the one-element page a section adapts into (see
// routes/…/section/edit/preview.js). A read-only screen that shows you a
// serialised blob and a button to go and see the real thing is a click in front
// of the only part anybody came to look at.
//
// Client-side on purpose, and still client-side now that something does serve
// it: a preview that needs a Lambda deploy to change is a preview nobody
// iterates on. What it shares with the public renderer is @common/render —
// literally the same modules, compiled into both bundles — so this is the same
// document api/code/src/render answers with, and this file only fetches and
// frames it.
//
// It builds the whole DOCUMENT, unlike the editor's live pane, which mounts the
// composed parts into a frame it already has. That is the difference between
// the two previews and it is deliberate: the shell — head, body template, and
// the style and script injected around it — is what a site's configuration is,
// and this is where you see it applied.
export const Composed = ({ siteId, page, height = "70vh" }) => {
  const client = useApolloClient();

  const [state, setState] = React.useState({ loading: true, html: null, error: null });

  // Keyed on the ids and on the page's own content, so editing a section and
  // coming back re-renders rather than showing what was composed last time.
  //
  // By VALUE, which is what lets a caller hand in a freshly built object every
  // render — the section screen composes one out of the row it is showing —
  // without the effect below running forever.
  const sectionIds = PageRender.referencedSectionIds(page);
  const key = JSON.stringify([siteId, sectionIds, page]);

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        // The site, for its configuration — which IS the document this page is
        // composed into: head, body template, and the script and style injected
        // around it. Asked for by query rather than read off the section
        // context, even though the site route resolved it three levels up:
        // every context between here and there SPREADS its parent's `values`,
        // so walking up for "the one that looks like a site" finds whichever
        // ancestor last set it and cannot tell a page from a site by shape.
        //
        // Not a round trip. The site route ran this exact query with these
        // exact variables, so cache-first — the default — answers it out of the
        // Apollo cache, and the refetch on the site's own screen is what makes
        // a saved shell show up here.
        const siteResult = await client.query({
          query: SiteQL.get,
          variables: { siteId },
        });
        const site = _get(siteResult, "data.viewer.account.sites.get");

        // One query per distinct section. There is no batch `get` on the API,
        // and a `list` would be one round trip but would page at 25 and pull
        // every section in the organization to render a page that uses two.
        // In parallel rather than in sequence, which is the part the archived
        // project got wrong: it awaited each section in a loop inside the
        // request path.
        //
        // A section previewing ITSELF references nothing, so this is an empty
        // list there and no request is made.
        const results = await Promise.all(
          sectionIds.map((sectionId) =>
            client
              .query({ query: SectionQL.get, variables: { siteId, sectionId } })
              .then((res) => [
                sectionId,
                _get(res, "data.viewer.account.sites.get.sections.get"),
              ])
              // One section that could not be read must not blank the whole
              // preview — it renders in place, where it stood. Which message it
              // renders is the point: a removal and a failed fetch both arrive
              // here as a rejection, and calling the second one "Section not
              // found" sends somebody looking for a section that is still
              // there. See sectionUnavailable in @common/render/page.
              .catch((error) => [
                sectionId,
                PageRender.sectionUnavailable(error),
              ])
          )
        );

        if (cancelled) {
          return;
        }

        const sections = {};
        for (const [sectionId, section] of results) {
          if (section) {
            sections[sectionId] = section;
          }
        }

        // The Editor-tab params, as the frame uses them, and the blog data they
        // select — a `/blog/:slug` page previews the post its author typed a
        // slug for, or "Post not found" when they typed none.
        const params = PageRender.editorParams(page);
        const data = await fetchBlogData(client, siteId, page, params);
        if (cancelled) {
          return;
        }
        setState({
          loading: false,
          error: null,
          html: PageRender.render(page, sections, {
            site,
            params,
            data,
            // This composes on the APP's hostname, into a sandboxed frame whose
            // origin is opaque — so it sends no cookie and there is no hostname
            // to resolve a file from. Both halves of P193, and the same answer
            // an anonymous visitor to /site/<orgId>/<siteId>/ gets.
            fileBase:
              site && site.orgId && site.siteId
                ? siteFileBaseOf(site.orgId, site.siteId)
                : FILE_PREFIX,
          }),
        });
      } catch (error) {
        if (!cancelled) {
          setState({ loading: false, error: error.toString(), html: null });
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (state.loading) {
    return <Loading.Icon />;
  }

  if (state.error) {
    return <div className="alert alert-danger">{state.error}</div>;
  }

  return (
    <iframe
      title="Preview"
      // A section's script is written by a tenant and runs here. The sandbox is
      // what stops it: allow-scripts without allow-same-origin gives it a
      // unique opaque origin, so it cannot read this app's cookies,
      // localStorage or session — which is the whole reason the preview is an
      // iframe rather than a div.
      sandbox="allow-scripts"
      srcDoc={state.html}
      style={{
        width: "100%",
        height,
        border: "1px solid #dee2e6",
        background: "#fff",
      }}
    />
  );
};
